/* eslint-disable no-bitwise */
import { createSlice } from '@reduxjs/toolkit';
import {
  NoPermissions,
  PermissionKeys,
  PermissionLevel,
  PermissionType,
  RoleType,
  RoleCheck,
} from 'constants/userConstants';
import { useSelector } from 'react-redux';
import queryString from 'query-string';
import * as R from 'ramda';
import { deepEqual } from 'swipeclock-frontend';

const initialState = {
  invalidToken: true,
  whiteLabel: queryString.parse(window.location.search).wl || '',
  token: undefined,
  siteSetupStage: '',
  numSiteSetupStages: 5,
  masterData: undefined,
  isTWX: false,
  isCentralized: false,
  usingEmployeeRole: true,
  usingLeadershipRole: false,
  isEmployeeExclusive: true,
  hasEmployeeRole: true,
  hasLeadershipRole: false,
  hasManagerRole: false,
  hasSupervisorRole: false,
  usesTWPSettings: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUserState() {
      return initialState;
    },
    invalidateToken(state) {
      state.invalidToken = true;
    },
    forceEmployeeRole(state, { payload }) {
      return {
        ...state,
        usingEmployeeRole: usingEmployeeRole(payload, true),
        usingLeadershipRole: usingLeadershipRole(payload, false),
      };
    },
    revertForcedEmployeeRole(state, { payload }) {
      return {
        ...state,
        usingEmployeeRole: usingEmployeeRole(payload, false),
        usingLeadershipRole: usingLeadershipRole(payload, true),
      };
    },
    sessionExpired(state) {
      return {
        ...state,
        invalidToken: true,
      };
    },
    setMasterData(state, action) {
      return {
        ...state,
        masterData: masterData({ masterData: action.payload }),
      };
    },
    setUser(state, action) {
      const {
        payload: { user, token },
      } = action;
      const { isCentralized, masterCompanyId, userSiteMapId, ...data } = user;
      const employeeExclusive = isEmployeeExclusive(user);
      const leadership = hasLeadershipRole(user);
      const roleEmployee = hasEmployeeRole(user);
      const roleManager = hasManagerRole(user);
      const roleSupervisor = hasSupervisorRole(user);

      return {
        ...state,
        ...data,
        invalidToken: false,
        token: token,
        isEmployeeExclusive: isEmployeeExclusive(user),
        isTWX: isTWX(token),
        hasEmployeeRole: roleEmployee,
        hasLeadershipRole: leadership,
        hasManagerRole: roleManager,
        hasSupervisorRole: roleSupervisor,
        masterData: masterData(user),
        usesTWPSettings: usesTWPSettings(user),
        usingEmployeeRole: usingEmployeeRole(user, employeeExclusive),
        usingLeadershipRole: usingLeadershipRole(user, leadership),
        userSiteMapId,
        masterCompanyId,
        isCentralized: !!isCentralized && Number.isInteger(masterCompanyId),
      };
    },
  },
});

export const {
  invalidateToken,
  forceEmployeeRole,
  resetUserState,
  revertForcedEmployeeRole,
  sessionExpired,
  setMasterData,
  setUser,
} = userSlice.actions;

export const rolesSelector = R.path(['user', 'roles']);

export function useUserRoles() {
  return useSelector(rolesSelector, deepEqual);
}

export default userSlice.reducer;

/**
 * Below are helper methods for working with
 * assigning roles and permissions.
 *
 * Note:
 * For Roles in WFH we have the following:
 * Manager, SiteAdmin, PARTNER, and Super Admin.
 *
 * WFH Roles line up with the following TWP Account Types
 *  -  Manager -> Supervisor
 *  -  SiteAdmin -> Client
 *  -  Partner -> Accountant
 *  -  SuperAdmin -> Administrator
 *
 */

export const isTWX = (user) => !!user?.userSiteMapId;

function assignRoles(user) {
  // If users:roles comes back falsey then we make
  // only allow them to see employee level
  const roles = user?.roles ?? ['Employee'];
  return roles.reduce((assignedValues, value) => {
    if (value === RoleType.EMPLOYEE) {
      // This uses the bitwise | to form a union
      return assignedValues | RoleCheck.hasEmployee;
    }

    if (value === RoleType.MANAGER) {
      return assignedValues | RoleCheck.hasManager;
    }

    if (value === RoleType.SITEADMIN) {
      return assignedValues | RoleCheck.hasSiteAdmin;
    }

    if (value === RoleType.PARTNER) {
      return assignedValues | RoleCheck.hasPartner;
    }

    if (value === RoleType.SUPERADMIN) {
      return assignedValues | RoleCheck.hasSuperAdmin;
    }

    return assignedValues;
  }, 0);
}

/**
 * Check if a user has any level of leadership meaning the user
 * has any role higher than "Employee" level access
 * @param {User} user The User object created from a JWT
 * @return {Boolean} The boolean to show if user has any leadership level
 */
export function hasLeadershipRole(user) {
  const value = assignRoles(user);
  const leadership =
    value &
    (RoleCheck.hasManager |
      RoleCheck.hasSiteAdmin |
      RoleCheck.hasPartner |
      RoleCheck.hasSuperAdmin);
  return !!leadership;
}

/**
 *  Check if a user has Manager level leadership
 *  A Manager is associated with the user role of "SiteAdmin"
 *  If a "SiteAdmin" aka Manager then user has Admin priviledge
 *  Note:
 *  We only check for SiteAdmin Role. If there are other leadership
 *  roles, we ignore and assume user gets highest leadership role of SiteAdmin
 * @param {User} user The User object created from a JWT
 * @return {Boolean} The boolean to show if user has Manager level leadership
 */
function hasManagerRole(user) {
  const value = assignRoles(user);
  const isSiteAdmin = value & RoleCheck.hasSiteAdmin;
  return !!isSiteAdmin;
}

/**
 *  Check if a user has Supervisor level leadership
 *  A Supervisor is associated with the user role of "Manager"
 *  This role of Manager is Not the same as a "SiteAdmin" Manager
 *  This role is considered low-level management aka Supervisor
 *  Note:
 *  We check to make sure supervisor does not contain higher SiteAdmin
 * @param {User} user The User object created from a JWT
 * @return {Boolean} The boolean to show if user has Manager level leadership
 */
function hasSupervisorRole(user) {
  const value = assignRoles(user);
  // eslint-disable-next-line no-shadow
  const isManager = value & RoleCheck.hasManager;
  const isSiteAdmin = value & RoleCheck.hasSiteAdmin;
  return !!(isManager && !isSiteAdmin);
}

/**
 * Check if a user has only the employee role
 * @param {User} user The User object created from a JWT
 * @return {Boolean} The boolean to show if user is an Employee
 */
function isEmployeeExclusive(user) {
  const value = assignRoles(user);
  const leadership = hasLeadershipRole(user);
  const employee = value & RoleCheck.hasEmployee;
  return !!employee && !leadership;
}

/**
 * Checks if the user has Employee level NOT exclusive access
 * Note:
 * This does not check if user contains any additional
 * level of leadership
 * @param {User} user The User object created from a JWT
 * @return {Boolean} The boolean to show if user has Employee level
 */
function hasEmployeeRole(user) {
  const value = assignRoles(user);
  const employee = value & RoleCheck.hasEmployee;
  return !!employee;
}

/**
 * Checks if the user has Super Admin access
 * @param {User} user The User object created from a JWT
 * @return {Boolean} The boolean to show if user has Employee level
 */
function hasSuperAdminRole(user) {
  const value = assignRoles(user);
  const isSuperAdmin = value & RoleCheck.hasSuperAdmin;
  return !!isSuperAdmin;
}

/**
 * Checks if the user has Partner level access
 * @param {User} user The User object created from a JWT
 * @return {Boolean} The boolean to show if user has Employee level
 */
function hasPartnerRole(user) {
  const value = assignRoles(user);
  const isPartner = value & RoleCheck.hasPartner;
  return !!isPartner;
}

/**
 * Checks if the user has Manager leadership
 * Checks to see if the site is Centralized
 * Currently these two values must be met to use "TWP" settings
 * If not then we assume the site uses TWX settings
 * TWP settings is only in scope as of 10/8/2020 to accept WFH centeralized sites
 * @param {User} user The User object created from a JWT
 * @return {Boolean} The boolean to show if user should use TWP settings
 */
function usesTWPSettings(user) {
  // For clarity check if isTWX first
  if (isTWX(user)) {
    return false;
  }

  const manager = hasManagerRole(user);
  const superAdmin = hasSuperAdminRole(user);
  const partner = hasPartnerRole(user);

  return (manager || superAdmin || partner) && user.isCentralized;
}

/**
 * Checks if the user has Manager leadership
 * If they have leadership and the "show" bool is true
 * Then usingLeadeershipRole will ensure the program runs
 * as a user with leadership
 * @param {User} user The User object created from a JWT
 * @param {Boolean} show This is a boolean to allow the role to be forced to use
 * @return {Boolean} The boolean to force the program to run as leadership
 */
function usingLeadershipRole(user, show) {
  const leadership = hasLeadershipRole(user);
  return leadership && show;
}

/**
 * Checks if the user is an employee
 * If they have employee and the "show" bool is true
 * Then usingEmployeeRole will ensure the program runs
 * as a user with employee
 * @param {User} user The User object created from a JWT
 * @param {Boolean} show This is a boolean to allow the role to be forced to use
 * @return {Boolean} The boolean to force the program to run as employee
 */
function usingEmployeeRole(user, show) {
  const employee = hasEmployeeRole(user);
  return employee && show;
}

/**
 * To Check permissions we are returning masterData with modified permissions
 * instead of giving us string values, we now can check permissions based off
 * of a bool value.
 *
 * IE:
 *     const { permissions } = user.masterData
 *     if (permissions.modifySchedule.all) {
 *        // Do something awesome
 *     }
 */
function masterData(user) {
  // Note: Features are site wide meaning the site supports the feature
  //       Permissions are for the current user
  //       For each item we need to check if site supports it and
  //       then need to verify the user has permission

  function modifyPermissionLevel(type) {
    // feature to permission, but currently this is not a thing
    // inside manager portal we can assume the features offered
    // are inside the scope of time and labor but eventually may need to check

    // ie
    // const featureMatch = features.find(
    //   x => x.id === Feature.standardScheduling
    // );

    const { permissions } = user.masterData || {};
    const permissionMatch = (permissions || []).find(
      (x) => x.permissionType === PermissionType[type]
    );

    if (permissionMatch) {
      const value = PermissionKeys.reduce((obj, key) => {
        const lvl = permissionMatch.permissionLevel === PermissionLevel[key];
        return { ...obj, [key]: lvl };
      }, {});

      return { [type]: value };
    }

    return {
      [type]: NoPermissions,
    };
  }

  const modifiedPermissions = Object.keys(PermissionType).reduce((obj, key) => {
    return { ...obj, ...modifyPermissionLevel(key) };
  }, {});

  return {
    ...user.masterData,
    permissions: modifiedPermissions,
  };
}

// Define a simple selector to get the user state
const selectUserState = (state) => state.user;

// TODO: Decide if we want to use createSelector
// It is not currently being used because it is not modifying the state.
//
// Use createSelector to create a memoized selector
// export const selectUser = createSelector(selectUserState, (user) => user);

export function useUser() {
  return useSelector(selectUserState);
}
